<template>
  <router-link :to="`/subcategory/${subcategory.id}`" class="card-link">
    <div class="subcategory-card">
      <!-- Badge -->
      <div v-if="badgeType" class="badge-container">
        <div class="badge" :class="badgeClasses">
          <div class="badge-content">
            <svg class="badge-icon" viewBox="0 0 24 24" fill="currentColor">
              <path :d="badgeIcon" />
            </svg>
            <span class="badge-text">{{ badgeText }}</span>
          </div>
        </div>
      </div>

      <!-- Image Container -->
      <div class="image-container">
        <div class="image-background"></div>
        <img
            :src="getImageUrl(subcategory)"
            :alt="subcategory.libelle"
            class="subcategory-image"
            @error="handleImageError"
        />
      </div>

      <!-- Content -->
      <div class="card-content">
        <h3 class="card-title">
          {{ subcategory.libelle }}
        </h3>
        <p v-if="subcategory.description" class="card-description">
          {{ subcategory.description }}
        </p>

        <!-- Type Indicators -->
        <div v-if="hasMultipleTypes" class="type-indicators">
          <span v-if="subcategory.best_sellers_count > 0" class="indicator bestseller" title="Best Sellers"></span>
          <span v-if="subcategory.limited_offers_count > 0" class="indicator limited" title="Limited Offers"></span>
          <span v-if="subcategory.new_arrivals_count > 0" class="indicator new" title="New Arrivals"></span>
        </div>
      </div>

      <!-- Hover Arrow -->
      <div class="hover-arrow">
        <div class="arrow-circle">
          <svg viewBox="0 0 24 24" fill="currentColor" class="arrow-icon">
            <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"/>
          </svg>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'SubcategoryCard',
  props: {
    subcategory: {
      type: Object,
      required: true
    },
    badgeType: {
      type: String,
      default: null,
      validator: (value) => ['best-seller', 'limited-offer', 'new-arrival','favorite', null].includes(value)
    }
  },
  setup(props) {
    const getImageUrl = (item) => {
      if (!item) return '/placeholder-image.png';
      return item.image_url || `/images/${item.image}`;
    };

    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };

    const badgeClasses = computed(() => {
      switch (props.badgeType) {
        case 'best-seller':
          return 'bestseller';
        case 'limited-offer':
          return 'limited-offer';
        case 'new-arrival':
          return 'new-arrival';
          case 'favorite':
          return 'favorite';
        default:
          return '';
      }
    });

    const badgeIcon = computed(() => {
      switch (props.badgeType) {
        case 'best-seller':
          return 'M5 16L3 5h5.5l1.5 5 1.5-5H16l-2 11H5zM12 2l3.09 6.26L22 9l-5 4.87 1.18 6.88L12 17.77l-6.18 2.98L7 14.87 2 9l6.91-1.74L12 2z';
        case 'limited-offer':
          return 'M7,2V4H8V18A4,4 0 0,0 12,22A4,4 0 0,0 16,18V4H17V2H7M11,16C10.4,16 10,15.6 10,15C10,14.4 10.4,14 11,14C11.6,14 12,14.4 12,15C12,15.6 11.6,16 11,16M13,12C12.4,12 12,11.6 12,11C12,10.4 12.4,10 13,10C13.6,10 14,10.4 14,11C14,11.6 13.6,12 13,12M11,8C10.4,8 10,7.6 10,7C10,6.4 10.4,6 11,6C11.6,6 12,6.4 12,7C12,7.6 11.6,8 11,8Z';
        case 'new-arrival':
          return 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4V12L16,16L17.4,14.6L14,11.2V4H12Z';
        case 'favorite':
          return 'M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z';
        default:
          return '';
      }
    });


    const badgeText = computed(() => {
      switch (props.badgeType) {
        case 'best-seller':
          return 'Popular';
        case 'limited-offer':
          return 'Limited';
        case 'new-arrival':
          return 'New';
        case 'favorite':
          return 'Favorite';  // Add this line
        default:
          return '';
      }
    });


    const hasMultipleTypes = computed(() => {
      const types = [
        props.subcategory.best_sellers_count > 0,
        props.subcategory.limited_offers_count > 0,
        props.subcategory.new_arrivals_count > 0
      ];
      return types.filter(Boolean).length > 1;
    });

    return {
      getImageUrl,
      handleImageError,
      badgeClasses,
      badgeIcon,
      badgeText,
      hasMultipleTypes
    };
  }
}
</script>

<style scoped>
/* Import modern fonts */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* Base styles */
.card-link {
  display: block;
  text-decoration: none;
  color: inherit;
  outline: none; /* Prevent focus outline on link */
}

.subcategory-card {
  background-color: var(--color-background);
  border: 1px solid var(--color-background-lighter);
  border-radius: 1rem;
  padding: 1.5rem; /* Increased from 1.25rem */
  height: 100%;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  min-height: 220px; /* Increased from 200px */
}

/* Dark mode - subtle grey background */
.dark .subcategory-card {
  background-color: #1a1a1a; /* Subtle grey instead of pure black */
  border-color: #2a2a2a;
}

.subcategory-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
  rgba(96, 0, 255, 0.02) 0%,
  rgba(0, 150, 36, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.dark .subcategory-card::before {
  background: linear-gradient(135deg,
  rgba(0, 230, 118, 0.05) 0%,
  rgba(0, 200, 83, 0.02) 100%);
}

.subcategory-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
  border-color: var(--color-primary);
}

.dark .subcategory-card:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.4);
  background-color: #252525; /* Slightly lighter on hover */
}

.subcategory-card:hover::before {
  opacity: 1;
}

/* Badge */
.badge-container {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem; /* Increased from 0.75rem */
}

.badge {
  border-radius: 0.75rem;
  padding: 0;
  font-size: 0.75rem; /* Increased from 0.65rem */
  font-weight: 600;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  transform: rotate(-1deg);
  transition: all 0.3s ease;
}

.badge:hover {
  transform: rotate(-1deg) scale(1.05);
}

.badge-content {
  display: flex;
  align-items: center;
  gap: 0.375rem; /* Increased from 0.25rem */
  padding: 0.5rem 0.75rem; /* Increased from 0.375rem 0.625rem */
  position: relative;
  z-index: 2;
}

.badge-icon {
  width: 0.875rem; /* Increased from 0.75rem */
  height: 0.875rem;
}

.badge-text {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge.favorite {
  background: linear-gradient(135deg, #ec4899, #db2777);
  color: white;
}

/* Badge variants */
.badge.bestseller {
  background: linear-gradient(135deg, #fef3c7, #fbbf24);
  color: #92400e;
}

.badge.limited-offer {
  background: linear-gradient(135deg, #fecaca, #f87171);
  color: #991b1b;
}

.badge.new-arrival {
  background: linear-gradient(135deg, #d1fae5, #34d399);
  color: #065f46;
}

/* Image Container - BIGGER */
.image-container {
  position: relative;
  width: 5rem; /* Increased from 3.5rem */
  height: 5rem; /* Increased from 3.5rem */
  margin: 0 auto 1rem auto; /* Increased margin */
  transition: all 0.3s ease;
}

.image-background {
  position: absolute;
  inset: 0;
  background: var(--color-background-light);
  border-radius: 1rem; /* Increased from 0.75rem */
  transition: all 0.3s ease;
}

.subcategory-image {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 1rem; /* Increased from 0.75rem */
  transition: all 0.3s ease;
}

.subcategory-card:hover .subcategory-image {
  transform: scale(1.1);
}

.subcategory-card:hover .image-background {
  background: var(--color-background-lighter);
}

/* Card Content */
.card-content {
  text-align: center;
  position: relative;
  z-index: 2;
}

.card-title {
  font-size: 1rem; /* Increased from 0.875rem */
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem; /* Increased from 0.375rem */
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}

.subcategory-card:hover .card-title {
  color: var(--color-primary);
}

.card-description {
  color: var(--color-text-secondary);
  font-size: 0.875rem; /* Increased from 0.75rem */
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Type Indicators */
.type-indicators {
  display: flex;
  justify-content: center;
  gap: 0.25rem; /* Increased from 0.1875rem */
  margin-top: 0.75rem; /* Increased from 0.5rem */
}

.indicator {
  width: 0.5rem; /* Increased from 0.375rem */
  height: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.indicator.bestseller {
  background-color: #f59e0b;
}

.indicator.limited {
  background-color: #ef4444;
}

.indicator.new {
  background-color: var(--color-primary);
}

.indicator:hover {
  transform: scale(1.2);
}

/* Hover Arrow */
.hover-arrow {
  position: absolute;
  bottom: 1rem; /* Increased from 0.75rem */
  right: 1rem; /* Increased from 0.75rem */
  opacity: 0;
  transform: translateX(0.375rem);
  transition: all 0.3s ease;
}

.subcategory-card:hover .hover-arrow {
  opacity: 1;
  transform: translateX(0);
}

.arrow-circle {
  width: 1.5rem; /* Increased from 1.25rem */
  height: 1.5rem;
  background-color: var(--color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.arrow-icon {
  width: 0.75rem; /* Increased from 0.625rem */
  height: 0.75rem;
  color: white;
}

/* Mobile specific adjustments */
@media (max-width: 768px) {
  .subcategory-card {
    padding: 1.25rem; /* Increased from 1rem */
    min-height: 180px; /* Increased from 160px */
  }

  .image-container {
    width: 4rem; /* Increased from 3rem */
    height: 4rem;
    margin-bottom: 0.75rem; /* Increased from 0.5rem */
  }

  .card-title {
    font-size: 0.9375rem; /* Increased from 0.8125rem */
    margin-bottom: 0.375rem; /* Increased from 0.25rem */
  }

  .card-description {
    font-size: 0.8125rem; /* Increased from 0.6875rem */
    -webkit-line-clamp: 1;
  }

  .badge-content {
    padding: 0.375rem 0.625rem; /* Increased from 0.25rem 0.5rem */
  }

  .badge-text {
    font-size: 0.6875rem; /* Increased from 0.6rem */
  }

  .badge-icon {
    width: 0.75rem; /* Increased from 0.625rem */
    height: 0.75rem;
  }

  .hover-arrow {
    bottom: 0.75rem; /* Increased from 0.5rem */
    right: 0.75rem;
  }

  .arrow-circle {
    width: 1.25rem; /* Increased from 1rem */
    height: 1.25rem;
  }

  .arrow-icon {
    width: 0.625rem; /* Increased from 0.5rem */
    height: 0.625rem;
  }
}

@media (max-width: 640px) {
  .subcategory-card {
    padding: 1rem; /* Increased from 0.75rem */
    min-height: 160px; /* Increased from 140px */
  }

  .image-container {
    width: 3.5rem; /* Increased from 2.5rem */
    height: 3.5rem;
  }

  .card-title {
    font-size: 0.875rem; /* Increased from 0.75rem */
  }

  .card-description {
    font-size: 0.75rem; /* Increased from 0.625rem */
  }
}

@media (max-width: 360px) {
  .subcategory-card {
    padding: 0.875rem; /* Increased from 0.625rem */
    min-height: 140px; /* Increased from 120px */
  }

  .image-container {
    width: 3rem; /* Increased from 2.25rem */
    height: 3rem;
  }

  .card-title {
    font-size: 0.8125rem; /* Increased from 0.6875rem */
    -webkit-line-clamp: 1;
  }

  .card-description {
    display: none;
  }

  .type-indicators {
    margin-top: 0.5rem; /* Increased from 0.25rem */
  }
}

/* Focus states for accessibility - Fixed to prevent group selection */
.card-link:focus .subcategory-card {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.card-link:focus {
  outline: none;
}

/* Prevent text selection on touch devices */
.subcategory-card {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .subcategory-card,
  .badge,
  .subcategory-image,
  .hover-arrow {
    transition: none;
  }
}
</style>
